import React, { useCallback, useState, useContext } from 'react';
import { Flex } from '@chakra-ui/react';
import {
  GetPropsCommonOptions,
  UseSelectGetItemPropsOptions,
  UseSelectGetMenuPropsOptions,
} from 'downshift';
import assoc from 'ramda/src/assoc';

import { SelectMeta, SelectOptions } from '../types';
import SelectContext, { ROOT_PARENT_ID } from '../Context';

import SelectGroup from './SelectGroup';
import SelectItem from './SelectItem';
import SelectGroupLabel from './SelectGroupLabel';

interface SelectDropdownProps {
  getMenuProps: (
    options?: UseSelectGetMenuPropsOptions | undefined,
    otherOptions?: GetPropsCommonOptions | undefined,
  ) => any;

  getItemProps: (options: UseSelectGetItemPropsOptions<SelectOptions>) => any;

  data: SelectOptions[];
  isOpen: boolean;
  meta?: SelectMeta;
  parentKey?: string;
  forceGroupSelections: boolean;
  dark: boolean;
}

const SelectDropdown = ({
  getMenuProps,
  isOpen,
  data,
  getItemProps,
  meta: groupMeta,
  parentKey = ROOT_PARENT_ID,
  forceGroupSelections,
  dark,
}: SelectDropdownProps) => {
  const [openSubgroupId, setOpenSubgroupId] = useState<{
    [index: string]: boolean;
  }>({});

  const { selectionData, setSelectionData } = useContext(SelectContext);

  const onItemClick = useCallback(
    (event: React.MouseEvent<HTMLElement> | React.ChangeEvent<HTMLElement>) => {
      event.stopPropagation();

      const { value, type } = event.target as HTMLInputElement;
      if (!value) {
        return;
      }

      const isRadioButton = type === 'radio';

      const { selectedGroupIds } = selectionData;
      const groupToUpdate = selectedGroupIds[parentKey] || {};
      const valueToUpdate = groupToUpdate[value] || false;
      const updatedValue = isRadioButton ? true : !valueToUpdate;

      const updatedGroup = assoc(
        value,
        updatedValue,
        isRadioButton ? {} : groupToUpdate,
      );

      const updatedSelectedIds = assoc(
        parentKey,
        updatedGroup,
        forceGroupSelections ? {} : selectedGroupIds,
      );

      setSelectionData({
        parentKey,
        selectedGroupIds: updatedSelectedIds,
      });
    },
    [parentKey, selectionData, setSelectionData, forceGroupSelections],
  );

  const onExpandableClick = (key: string) =>
    setOpenSubgroupId((previousState) =>
      assoc(key, !openSubgroupId[key], previousState),
    );

  return isOpen ? (
    <SelectGroup label={groupMeta?.label} dark={dark}>
      {data.map((option, optionIndex) => {
        const isItemChecked =
          selectionData.selectedGroupIds[parentKey] &&
          selectionData.selectedGroupIds[parentKey][option.key];

        return (
          <React.Fragment key={option.key}>
            {option.meta?.groupLabel && (
              <SelectGroupLabel isFirstGroup={optionIndex === 0} dark={dark}>
                {option.meta.groupLabel}
              </SelectGroupLabel>
            )}
            {/* eslint-disable-next-line */}
            <Flex display="flex" ml={parentKey === ROOT_PARENT_ID ? 0 : 4}>
              <SelectItem
                dark={dark}
                getMenuProps={getMenuProps}
                getItemProps={getItemProps}
                isOpen={openSubgroupId[option.key]}
                onExpandableClick={onExpandableClick}
                option={option}
                onItemClick={onItemClick}
                parentKey={parentKey}
                groupMeta={groupMeta}
                isChecked={isItemChecked}
                forceGroupSelections={forceGroupSelections}
              />
            </Flex>
          </React.Fragment>
        );
      })}
    </SelectGroup>
  ) : null;
};

export default SelectDropdown;
